import { Suspense, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  graphql,
  useFragment,
  useMutation,
  useRelayEnvironment,
} from "react-relay";
import { logger } from "@/common/logger";
import {
  schemaFactory,
  WorkspaceMetadataEditForm,
} from "@/components/WorkspaceMetadataEdit";
import { Edit03Icon, XIcon } from "@/icons";
import { Button, ButtonProps } from "@/kit/ui/button";
import { Form } from "@/kit/ui/form";
import {
  ResponsiveSheet,
  ResponsiveSheetBody,
  ResponsiveSheetClose,
  ResponsiveSheetContent,
  ResponsiveSheetFooter,
  ResponsiveSheetHeader,
  ResponsiveSheetPortal,
  ResponsiveSheetTitle,
  ResponsiveSheetTrigger,
} from "@/kit/ui/responsive-sheet";
import { preprocessEdit, relayErrorMessage } from "@/utils/relay";
import { fetchAllTags } from "@/utils/taggable";
import { toast } from "@/utils/toast";
import { useValibotForm } from "@/utils/validation";
import { MutableRef } from "@/utils/react";
import { WorkspaceMetadataEditButtonFragment$key } from "./__generated__/WorkspaceMetadataEditButtonFragment.graphql";
import { WorkspaceMetadataEditButtonMutation } from "./__generated__/WorkspaceMetadataEditButtonMutation.graphql";
import { cn } from "@/utils/tailwind";

const Fragment = graphql`
  fragment WorkspaceMetadataEditButtonFragment on Workspace {
    id
    shortDescription
    license
    ...TagListFragment
    ...taggableFragment
    viewerCanEdit: viewerCan(action: UPDATE_WORKSPACE)
  }
`;

const Mutation = graphql`
  mutation WorkspaceMetadataEditButtonMutation(
    $id: ID!
    $input: UpdateWorkspaceInput!
  ) {
    updateWorkspace(id: $id, input: $input) {
      ...WorkspaceMetadataEditButtonFragment
    }
  }
`;

export interface WorkspaceMetadataEditButtonProps extends ButtonProps {
  workspace: WorkspaceMetadataEditButtonFragment$key;
}

export default function WorkspaceMetadataEditButton({
  workspace: workspaceKey,
  children,
  className,
  variant = "link",
  size = "sm",
  ...props
}: WorkspaceMetadataEditButtonProps) {
  const workspace = useFragment(Fragment, workspaceKey);
  const [isOpen, setOpen] = useState(false);
  const isBusy = useRef(false);

  if (!workspace.viewerCanEdit) {
    return null;
  }

  const onOpenChange = (open: boolean) => {
    if (isBusy.current) return;
    setOpen(open);
  };

  return (
    <ResponsiveSheet open={isOpen} onOpenChange={onOpenChange}>
      <ResponsiveSheetTrigger asChild>
        <Button
          {...props}
          className={cn("gap-1", className)}
          variant={variant}
          size={size}
        >
          {children || (
            <>
              <Edit03Icon className="w-5 h-5" />
              <FormattedMessage defaultMessage="Edit" />
            </>
          )}
        </Button>
      </ResponsiveSheetTrigger>

      <ResponsiveSheetPortal>
        <ResponsiveSheetContent className="h-full">
          <Suspense>
            <EditForm
              workspace={workspaceKey}
              isBusy={isBusy}
              onOpenChange={onOpenChange}
            />
          </Suspense>
        </ResponsiveSheetContent>
      </ResponsiveSheetPortal>
    </ResponsiveSheet>
  );
}

interface EditFormProps {
  workspace: WorkspaceMetadataEditButtonFragment$key;
  isBusy: MutableRef<boolean>;
  onOpenChange: (isOpen: boolean) => void;
}

function EditForm(props: EditFormProps) {
  const workspace = useFragment(Fragment, props.workspace);
  const intl = useIntl();
  const relay = useRelayEnvironment();
  const formSchema = useMemo(() => schemaFactory(intl), [intl]);
  const form = useValibotForm(formSchema, {
    mode: "onChange",
    defaultValues: async () => {
      const tags = await fetchAllTags(relay, workspace);
      return {
        shortDescription: workspace.shortDescription ?? "",
        tags: tags.map(({ node: { name } }) => name),
        license: workspace.license ?? null,
      };
    },
  });

  const [commitMutation, isMutationInFlight] =
    useMutation<WorkspaceMetadataEditButtonMutation>(Mutation);

  const onSubmit = form.handleSubmit((submitData) =>
    commitMutation({
      variables: {
        id: workspace.id,
        input: preprocessEdit(submitData, form.formState.defaultValues),
      },
      onCompleted: () => {
        toast.success(
          <FormattedMessage defaultMessage="Workspace has successfully been updated!" />,
        );
        props.onOpenChange(false);
      },
      onError: (error) => {
        logger.error(error);
        toast.error(relayErrorMessage(error));
      },
    }),
  );

  return (
    <Form form={form} onSubmit={onSubmit}>
      <ResponsiveSheetHeader className="flex flex-col sm:p-6 px-4 pt-4 pb-5 border-b border-t-grey-200">
        <div className="w-full flex justify-between items-center">
          <ResponsiveSheetTitle>
            <p className="text-xl font-semibold text-grey-900">
              <FormattedMessage defaultMessage="Edit dataset card" />
            </p>
          </ResponsiveSheetTitle>
          <ResponsiveSheetClose asChild>
            <Button
              variant="tertiary"
              size="md"
              hue="grey"
              icon
              disabled={isMutationInFlight}
            >
              <XIcon className="w-5 h-5" />
            </Button>
          </ResponsiveSheetClose>
        </div>
      </ResponsiveSheetHeader>

      <ResponsiveSheetBody className="w-full flex flex-col gap-6 flex-nowrap">
        <WorkspaceMetadataEditForm />
      </ResponsiveSheetBody>

      <ResponsiveSheetFooter className="flex flex-row justify-between items-center sm:p-6 px-4 pt-4 pb-8 border-t border-t-grey-200 gap-5">
        <ResponsiveSheetClose asChild>
          <Button
            size="md"
            variant="secondary"
            className="w-full"
            disabled={isMutationInFlight}
          >
            <FormattedMessage defaultMessage="Discard changes" />
          </Button>
        </ResponsiveSheetClose>

        <Button
          size="md"
          type="submit"
          disabled={
            isMutationInFlight ||
            !form.formState.isValid ||
            form.formState.isLoading
          }
          className="w-full"
        >
          <FormattedMessage defaultMessage="Save changes" />
        </Button>
      </ResponsiveSheetFooter>
    </Form>
  );
}
